import React, { useState, useEffect } from 'react';
import './DirectCourseSelection.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  setDirectCourses,
  clearMoreClassSelection,
  setQueryParams,
  fetchDirectAccessRequest,
  setPreviousStep,
} from '../../../redux/actions/WizardLaunchActions';
import { cancelDirectAccessRequest } from '../../../redux/actions/WizardLaunchActions';
import Banner from '../../common/Banner/Banner';
import { paypalPaymentSuccess } from '../../../redux/actions/PaypalActions';


function DirectCourseSelection({ setFormState }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const savedDirectDataAccess = useSelector(
    (state) => state.wizardLaunch.directAccess?.data || null
  );
  const directAccessData = useSelector(
    (state) => state.wizardLaunch.directAccess?.data || null
  );
  const directAccessLoading = useSelector(
    (state) => state.wizardLaunch.directAccess?.loading || false
  );
  const directAccessError = useSelector(
    (state) => state.wizardLaunch.directAccess?.error || null
  );
  const queryParams = useSelector((state) => state.wizardLaunch.queryParams);
  const existingDirectCourses = useSelector((state) => state.wizardLaunch?.directCourses) || [];
  const [addedItems, setAddedItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [basePrice, setBasePrice] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const offerAmount = directAccessData?.offer || '$0';

  // Handle query parameters from URL
  useEffect(() => {
    if (location.search) {
      // Get the raw query string without the leading '?'
      const rawQueryString = location.search.substring(1);
      const queryParamsObj = {};
      
      // Split by & and process each parameter manually
      rawQueryString.split('&').forEach(pair => {
        const [key, value] = pair.split('=');
        queryParamsObj[key] = value;
      });
      if(queryParamsObj.access){
        // Dispatch the action to process the access token in the saga
        dispatch(fetchDirectAccessRequest({ access: queryParamsObj.access }));
      }
    }
  }, [location.search, dispatch]);

  // Initialize addedItems with existing direct courses when data is loaded
  useEffect(() => {
    if (directAccessData?.options && existingDirectCourses.length > 0) {
      // Map existing courses to their section_ids
      const existingIds = existingDirectCourses.map(course => course.section_id);
      
      // Find matching courses in options
      const matchingCourses = directAccessData.options.filter(option => 
        existingIds.includes(option.section_id)
      );
      dispatch(paypalPaymentSuccess(null));
      // Set added items and calculate total
      const selectedIds = matchingCourses.map(course => course.section_id);
      
      // Use setTimeout to ensure state updates after render
      setTimeout(() => {
        setAddedItems(selectedIds);
        calculateTotal(selectedIds);
      }, 0);
    }
  }, [directAccessData, existingDirectCourses]);

  // Update UI when direct access data changes
  useEffect(() => {
    if (directAccessData?.options) {
      setAddedItems([]);
      setTotalAmount(0);
      setBasePrice(0);
      setDiscount(0);
    }
  }, [directAccessData]);

  useEffect(() => {
    dispatch(clearMoreClassSelection());
    
    // Return a cleanup function that will run when the component unmounts
    return () => {
      // Cancel any pending direct access requests to prevent the async message channel error
      dispatch(cancelDirectAccessRequest());
    };
  }, [dispatch]);
  const calculateTotal = (items) => {
    let base = 0;
    // Filter visible items
    const visibleItems = items.filter(id => {
      const endorsement = directAccessData?.options?.find(
        option => option.section_id === id
      );
      return endorsement;
    });

    // Calculate base price only for visible items
    visibleItems.forEach((id) => {
      const course = directAccessData?.options?.find(
        (option) => option.section_id === id
      );
      if (course) {
        base += parseFloat(course.price);
      }
    });

    setBasePrice(base);

    const numericOfferAmount = parseFloat(offerAmount.replace(/[^\d.]/g, '') || 0);
    const discountAmount = visibleItems.length > 1 ? (visibleItems.length - 1) * numericOfferAmount : 0;
    setDiscount(discountAmount);
    setTotalAmount(base - discountAmount);
  };

  const handleAddedClick = async (id) => {
    setIsProcessing(true);
    try {
      let newAddedItems;
      if (addedItems.includes(id)) {
        newAddedItems = addedItems.filter((items) => items !== id);
      } else {
        newAddedItems = [...addedItems, id];
      }
      setAddedItems(newAddedItems);
      calculateTotal(newAddedItems);

      // Get all direct courses
      const newDirectCourses = directAccessData?.options
        ?.filter((item) => {
          return newAddedItems.includes(item.section_id);
        })
        .map((item) => ({
          ...item,
          section_id: item.section_id,
        })) || [];

      dispatch(setDirectCourses(newDirectCourses));
      const directCourse = directAccessData?.options?.find(
        (item) => item.section_id === id
      );
      if (directCourse && typeof setFormState === 'function') {
        setFormState({
          question_id: directCourse.next_question_id,
          userAnswer: {
            question_answer_id: directCourse.question_answer_id,
            answer: newDirectCourses,
            next_question_id: directCourse.next_question_id,
          },
          question_type:
            directCourse.next_question_type ||
            directAccessData?.question_type ||
            'directcourses',
        });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGoToPay = () => {
    // Get all direct courses
    const totalDirectCourses = directAccessData?.options
      ?.filter(course => addedItems.includes(course.section_id))
      .map(course => ({
        id: course.section_id,
        name: course.name,
        price: course.price || "10.00",
        type: 'direct_course',
        imgUrl: course.imgUrl,
        question_answer_id: course.section_id,
        courseType: 'Direct Course',
        section_id: course.section_id
      })) || [];

    if (totalDirectCourses.length === 0) {
      setOpenDialog(true);
      return;
    }

    // Set the previous step to the current path
    dispatch(setPreviousStep('/direct-course-selection'));
    
    // First dispatch to Redux store
    dispatch(setDirectCourses(totalDirectCourses));
    
    // Then navigate to shopping cart
    navigate('/shoppingcart', { 
      state: { 
        directcourses: totalDirectCourses
      } 
    });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false); // Close the dialog
  };

  // Update the card rendering to force re-render when addedItems changes
  const renderCourseCard = (course) => {
    const isSelected = addedItems.includes(course.section_id);
    
    return (
      <div
        key={course.section_id}
        className={
          isSelected
            ? 'selected-card cards'
            : 'not-selected cards'
        }
      >
        <img src={course.imgUrl} alt='img' />
        <Typography className='title'>{course.name}</Typography>
        <Typography className='description' sx={{ marginTop: '10px' }}>
          {course.description}
        </Typography>
        <div className='bottom-sec'>
          <Typography className='price-lbl'>
            ${course.price}
          </Typography>
          <Button
            className={
              isSelected
                ? 'btn-added'
                : 'btn-add'
            }
            onClick={() => handleAddedClick(course.section_id)}
          >
            {isSelected ? 'Added' : 'Add'}
          </Button>
        </div>
      </div>
    );
  };

  if (directAccessLoading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='200px'
      >
        <CircularProgress />
      </Box>
    );
  }

  if (directAccessError) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='200px'
      >
        <Typography color='error'>
          {directAccessError}
        </Typography>
      </Box>
    );
  }

  return (
    <> 
    <Banner />

    <div className='endorsements-wrap'>
      <Typography 
        variant='h5'>
        {directAccessData?.question_text}
      </Typography>
      <Typography className='lbl-text'>
        You can select 1 or more options
      </Typography>
      <div className='card-wrap'>
        {directAccessData?.options.map(course => renderCourseCard(course))}
      </div>
      <div className='discount-wrap'>
        <Typography variant='h4'>Multiple Selection Discount!</Typography>
        <Typography>
          Save {offerAmount} on each additional selection when choosing multiple courses.
        </Typography>
      </div>

      <div className='priceSummary-sec'>
        <div className='priceSummary-wrap'>
          <Typography>
            <strong>Price Summary</strong>
          </Typography>
          <Typography className='border-bottom'>
            Selected courses:{' '}
            <span>
              <strong>
                {addedItems.length}
              </strong>
            </span>
          </Typography>
          <Typography>
            Base price:{' '}
            <span>
              <strong>${basePrice?.toFixed(2)} </strong>
            </span>
          </Typography>
          <Typography>
            Discount:{' '}
            <span>
              <strong>${discount?.toFixed(2)} </strong>
            </span>
          </Typography>
          <Typography className='total-txt'>
            <strong>Total: ${totalAmount?.toFixed(2)}</strong>
          </Typography>
        </div>
      </div>
      <div className='btn-wrap '>
        <Button className='btn-dark' onClick={handleGoToPay}>
          Go to Pay
        </Button>
      </div>

      {/* Dialog for no endorsement selected */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>No Course Selected</DialogTitle>
        <DialogContent>
          <Typography>
            Please select at least one Course before proceeding to pay.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color='primary'>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
    </>
  );
}

export default DirectCourseSelection;
